import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import {
  lightColors,
  darkColors,
  typography as sharedTypography,
  defaultFonts,
  borderRadius,
} from '@event-planner/shared';

/**
 * MUI themes using shared Flat Rock brand tokens.
 *
 * Font families:
 *   h1-h4 (display/headline) — Playfair Display (serif)
 *   h5-h6, body, labels, buttons — Inter (sans-serif)
 *
 * See docs/theme-token-roles.md for the color mapping table.
 */

const displayFont = defaultFonts.admin.display;
const bodyFont = defaultFonts.admin.body;

/**
 * Phone adjustments (below the 900 px `md` breakpoint, the same cutoff as
 * useIsPhone) applied app-wide through CSS media queries, so desktop
 * rendering is untouched. Dialogs use the full phone width with a 16 px
 * margin instead of MUI's 32 px, content padding tightens, and dialog
 * action buttons wrap instead of running off the edge.
 */
const PHONE = '@media (max-width: 899.95px)';
const phoneComponentOverrides: ThemeOptions['components'] = {
  MuiDialog: {
    styleOverrides: {
      paper: {
        [PHONE]: {
          '&:not(.MuiDialog-paperFullScreen)': {
            margin: 16,
            width: 'calc(100% - 32px)',
            maxWidth: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
          },
        },
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: { root: { [PHONE]: { padding: '16px 16px 8px' } } },
  },
  MuiDialogContent: {
    styleOverrides: { root: { [PHONE]: { padding: '8px 16px 16px' } } },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        [PHONE]: {
          flexWrap: 'wrap',
          gap: 8,
          padding: '8px 16px 16px',
          '& > :not(style) ~ :not(style)': { marginLeft: 0 },
        },
      },
    },
  },
};

export const lightTheme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: lightColors.primary,
      contrastText: lightColors.onPrimary,
    },
    secondary: {
      main: lightColors.secondary,
      contrastText: lightColors.onSecondary,
    },
    error: {
      main: lightColors.error,
      contrastText: lightColors.onError,
    },
    warning: {
      main: lightColors.warning,
      contrastText: lightColors.onWarning,
    },
    info: {
      main: lightColors.tertiary,
      contrastText: lightColors.onTertiary,
    },
    success: {
      main: lightColors.success,
      contrastText: lightColors.onSuccess,
    },
    background: {
      default: lightColors.background,
      paper: lightColors.surface,
    },
    text: {
      primary: lightColors.onSurface,
      secondary: lightColors.onSurfaceVariant,
      disabled: lightColors.outline,
    },
    divider: lightColors.outlineVariant,
    action: {
      hover: `${lightColors.primary}14`,
      selected: `${lightColors.primary}1F`,
      disabled: `${lightColors.onSurface}61`,
      focus: `${lightColors.primary}1F`,
    },
  },
  typography: {
    fontFamily: bodyFont,
    // Display — serif (brand)
    h1: {
      fontFamily: displayFont,
      fontSize: sharedTypography.displayLarge.fontSize,
      fontWeight: Number(sharedTypography.displayLarge.fontWeight),
      lineHeight: `${sharedTypography.displayLarge.lineHeight}px`,
      letterSpacing: sharedTypography.displayLarge.letterSpacing,
    },
    h2: {
      fontFamily: displayFont,
      fontSize: sharedTypography.displayMedium.fontSize,
      fontWeight: Number(sharedTypography.displayMedium.fontWeight),
      lineHeight: `${sharedTypography.displayMedium.lineHeight}px`,
    },
    // Headline — serif (brand)
    h3: {
      fontFamily: displayFont,
      fontSize: sharedTypography.headlineLarge.fontSize,
      fontWeight: Number(sharedTypography.headlineLarge.fontWeight),
      lineHeight: `${sharedTypography.headlineLarge.lineHeight}px`,
    },
    h4: {
      fontFamily: displayFont,
      fontSize: sharedTypography.headlineMedium.fontSize,
      fontWeight: Number(sharedTypography.headlineMedium.fontWeight),
      lineHeight: `${sharedTypography.headlineMedium.lineHeight}px`,
    },
    // Title — sans-serif (UI)
    h5: {
      fontFamily: bodyFont,
      fontSize: sharedTypography.titleLarge.fontSize,
      fontWeight: Number(sharedTypography.titleLarge.fontWeight),
      lineHeight: `${sharedTypography.titleLarge.lineHeight}px`,
    },
    h6: {
      fontFamily: bodyFont,
      fontSize: sharedTypography.titleMedium.fontSize,
      fontWeight: Number(sharedTypography.titleMedium.fontWeight),
      lineHeight: `${sharedTypography.titleMedium.lineHeight}px`,
    },
    // Body — sans-serif (readability)
    body1: {
      fontFamily: bodyFont,
      fontSize: sharedTypography.bodyLarge.fontSize,
      fontWeight: Number(sharedTypography.bodyLarge.fontWeight),
      lineHeight: `${sharedTypography.bodyLarge.lineHeight}px`,
      letterSpacing: sharedTypography.bodyLarge.letterSpacing,
    },
    body2: {
      fontFamily: bodyFont,
      fontSize: sharedTypography.bodyMedium.fontSize,
      fontWeight: Number(sharedTypography.bodyMedium.fontWeight),
      lineHeight: `${sharedTypography.bodyMedium.lineHeight}px`,
      letterSpacing: sharedTypography.bodyMedium.letterSpacing,
    },
    subtitle1: {
      fontFamily: bodyFont,
      fontSize: sharedTypography.titleMedium.fontSize,
      fontWeight: Number(sharedTypography.titleMedium.fontWeight),
      lineHeight: `${sharedTypography.titleMedium.lineHeight}px`,
    },
    subtitle2: {
      fontFamily: bodyFont,
      fontSize: sharedTypography.titleSmall.fontSize,
      fontWeight: Number(sharedTypography.titleSmall.fontWeight),
      lineHeight: `${sharedTypography.titleSmall.lineHeight}px`,
    },
    button: {
      fontFamily: bodyFont,
      fontSize: sharedTypography.labelLarge.fontSize,
      fontWeight: Number(sharedTypography.labelLarge.fontWeight),
      lineHeight: `${sharedTypography.labelLarge.lineHeight}px`,
      letterSpacing: sharedTypography.labelLarge.letterSpacing,
      textTransform: 'none',
    },
    caption: {
      fontFamily: bodyFont,
      fontSize: sharedTypography.bodySmall.fontSize,
      fontWeight: Number(sharedTypography.bodySmall.fontWeight),
      lineHeight: `${sharedTypography.bodySmall.lineHeight}px`,
    },
    overline: {
      fontFamily: bodyFont,
      fontSize: sharedTypography.labelSmall.fontSize,
      fontWeight: Number(sharedTypography.labelSmall.fontWeight),
      lineHeight: `${sharedTypography.labelSmall.lineHeight}px`,
      letterSpacing: sharedTypography.labelSmall.letterSpacing,
    },
  },
  shape: {
    borderRadius: borderRadius.md,
  },
  components: phoneComponentOverrides,
});

export const darkTheme: Theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: darkColors.primary,
      contrastText: darkColors.onPrimary,
    },
    secondary: {
      main: darkColors.secondary,
      contrastText: darkColors.onSecondary,
    },
    error: {
      main: darkColors.error,
      contrastText: darkColors.onError,
    },
    warning: {
      main: darkColors.warning,
      contrastText: darkColors.onWarning,
    },
    info: {
      main: darkColors.tertiary,
      contrastText: darkColors.onTertiary,
    },
    success: {
      main: darkColors.success,
      contrastText: darkColors.onSuccess,
    },
    background: {
      default: darkColors.background,
      paper: darkColors.surfaceContainer,
    },
    text: {
      primary: darkColors.onSurface,
      secondary: darkColors.onSurfaceVariant,
      disabled: darkColors.outline,
    },
    divider: darkColors.outlineVariant,
    action: {
      hover: `${darkColors.primary}14`,
      selected: `${darkColors.primary}1F`,
      disabled: `${darkColors.onSurface}61`,
      focus: `${darkColors.primary}1F`,
    },
  },
  typography: lightTheme.typography,
  shape: lightTheme.shape,
  components: phoneComponentOverrides,
});
